import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    MdClose, MdPerson, MdEmail, MdPhone, MdLock,
    MdVisibility, MdVisibilityOff, MdCheckCircle, MdAssignment, MdLocationCity
} from 'react-icons/md';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../context/LanguageContext';

const KVKK_TEXT_TR = `KİŞİSEL VERİLERİN KORUNMASI HAKKINDA AYDINLATMA METNİ

Nortech olarak, 6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) kapsamında kişisel verilerinizi aşağıda açıklanan amaçlar doğrultusunda işlemekteyiz.

1. VERİ SORUMLUSU
Nortech, veri sorumlusu sıfatıyla hareket etmektedir.

2. İŞLENEN KİŞİSEL VERİLER
- Ad Soyad / Firma Unvanı
- E-posta adresi
- Telefon numarası
- Kullanıcı adı
- Konum bilgisi (İl / İlçe)

3. KİŞİSEL VERİLERİN İŞLENME AMACI
Toplanan kişisel veriler; sistemin güvenli kullanımı, hesap yönetimi, iletişim, yasal yükümlülüklerin yerine getirilmesi amacıyla işlenmektedir.

4. KİŞİSEL VERİLERİN AKTARILMASI
Kişisel verileriniz; yasal zorunluluklar dışında üçüncü kişilerle paylaşılmamaktadır.

5. KİŞİSEL VERİLERİN SAKLANMA SÜRESİ
Kişisel verileriniz, hesap aktif olduğu süre ve sonrasında yasal saklama süreleri boyunca muhafaza edilecektir.

6. HAKLARINIZ
KVKK'nın 11. maddesi uyarınca; verilerinize erişim, düzeltme, silme, işlemenin kısıtlanması ve itiraz haklarına sahipsiniz.

7. İLETİŞİM
Haklarınızla ilgili taleplerini iletmek için sistem yöneticisine başvurabilirsiniz.

Bu formu doldurarak KVKK kapsamında kişisel verilerinizin yukarıda belirtilen amaçlarla işlenmesine onay vermiş olursunuz.`;

const KVKK_TEXT_EN = `INFORMATION TEXT ON THE PROTECTION OF PERSONAL DATA

As Nortech, we process your personal data in accordance with the Law on the Protection of Personal Data (KVKK) for the purposes described below.

1. DATA CONTROLLER
Nortech acts as the data controller.

2. PROCESSED PERSONAL DATA
- Name Surname / Company Name
- Email address
- Phone number
- Username
- Location info (Province / District)

3. PURPOSE OF PROCESSING PERSONAL DATA
Collected personal data is processed for secure system usage, account management, communication, and fulfilling legal obligations.

4. TRANSFER OF PERSONAL DATA
Your personal data is not shared with third parties except for legal obligations.

5. RETENTION PERIOD OF PERSONAL DATA
Your personal data will be kept for as long as the account is active and during subsequent legal retention periods.

6. YOUR RIGHTS
In accordance with Article 11 of the KVKK; you have the right to access, correct, delete, restrict processing, and object to the processing of your data.

7. CONTACT
You can contact the system administrator to submit your requests regarding your rights.

By filling out this form, you consent to the processing of your personal data within the scope of KVKK for the purposes specified above.`;

export default function RegisterModal({ onClose }) {
    const { t, language } = useLanguage();
    const [step, setStep] = useState(1); // 1=form, 'verify'=code entry, 2=success
    const [showKvkk, setShowKvkk] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [code, setCode] = useState('');
    const [codeError, setCodeError] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setInterval(() => {
            setResendCooldown(s => (s <= 1 ? 0 : s - 1));
        }, 1000);
        return () => clearInterval(timer);
    }, [resendCooldown]);

    const [form, setForm] = useState({
        username: '',       // Giriş kullanıcı adı
        companyHint: '',     // Bağlı olduğu firma (admin onayı için bilgi amaçlı)
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        kvkkAccepted: false
    });
    const [errors, setErrors] = useState({});

    const handleChange = (field, value) => {
        setForm(f => ({ ...f, [field]: value }));
        if (errors[field]) setErrors(e => ({ ...e, [field]: '' }));
    };

    const validate = () => {
        const e = {};
        if (!form.username.trim()) e.username = t('register.usernameRequired');
        else if (form.username.trim().length < 3) e.username = t('register.usernameMin');
        if (!form.companyHint.trim()) e.companyHint = t('register.companyRequired');
        if (!form.email.trim()) e.email = t('register.emailInvalid');
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t('register.emailInvalid');
        if (!form.phone.trim()) e.phone = t('register.phoneRequired');
        if (!form.password) e.password = t('register.passwordRequired');
        else if (form.password.length < 6) e.password = t('register.passwordMin');
        if (form.confirmPassword !== form.password) e.confirmPassword = t('register.passwordMismatch');
        if (!form.kvkkAccepted) e.kvkkAccepted = t('register.kvkkRequired');
        return e;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }

        setLoading(true);
        try {
            await api.post('/registrations/send-code', {
                email: form.email.trim(),
                language
            });
            toast.success(t('register.codeSentSuccess'));
            setCode('');
            setCodeError('');
            setResendCooldown(60);
            setStep('verify');
        } catch (err) {
            const msg = err.response?.data?.message || t('register.registerFailed');
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyAndSubmit = async (e) => {
        e.preventDefault();
        if (!code.trim() || code.trim().length !== 6) {
            setCodeError(t('register.invalidCode'));
            return;
        }

        const username = form.username.trim();

        setVerifying(true);
        setCodeError('');
        try {
            const verifyRes = await api.post('/registrations/verify-code', {
                email: form.email.trim(),
                code: code.trim()
            });
            const { verificationToken } = verifyRes.data.data;

            await api.post('/registrations', {
                companyName: form.companyHint.trim(),
                email: form.email.trim(),
                phone: form.phone.trim(),
                username,
                password: form.password,
                kvkkAccepted: form.kvkkAccepted,
                verificationToken
            });
            setStep(2);
        } catch (err) {
            const msg = err.response?.data?.message || t('register.invalidCode');
            setCodeError(msg);
        } finally {
            setVerifying(false);
        }
    };

    const handleResend = async () => {
        if (resendCooldown > 0) return;
        setVerifying(true);
        try {
            await api.post('/registrations/send-code', {
                email: form.email.trim(),
                language
            });
            toast.success(t('register.codeSentSuccess'));
            setResendCooldown(60);
        } catch (err) {
            toast.error(err.response?.data?.message || t('register.registerFailed'));
        } finally {
            setVerifying(false);
        }
    };

    const handleBackToForm = () => {
        setStep(1);
        setCode('');
        setCodeError('');
    };

    const kvkkText = language === 'tr' ? KVKK_TEXT_TR : KVKK_TEXT_EN;

    return (
        <AnimatePresence>
            <motion.div
                className="modal-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
                style={{ alignItems: 'flex-start', paddingTop: 24, paddingBottom: 24, overflowY: 'auto' }}
            >
                <motion.div
                    className="modal"
                    style={{ maxWidth: 520, width: '100%', margin: 'auto' }}
                    initial={{ opacity: 0, scale: 0.92, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.92, y: 20 }}
                    transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                >
                    {step === 2 ? (
                        /* Success State */
                        <div style={{ padding: '48px 32px', textAlign: 'center' }}>
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                                style={{
                                    width: 72, height: 72,
                                    borderRadius: '50%',
                                    background: 'rgba(16,185,129,0.15)',
                                    border: '2px solid rgba(16,185,129,0.4)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    margin: '0 auto 20px', fontSize: 36, color: 'var(--success)'
                                }}
                            >
                                <MdCheckCircle />
                            </motion.div>
                            <h2 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 12 }}>
                                {t('register.successTitle')}
                            </h2>
                            <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.7, marginBottom: 28 }}>
                                {t('register.registerSuccess')}
                            </p>
                            <button className="login-btn" style={{ maxWidth: 200, margin: '0 auto' }} onClick={onClose}>
                                {t('common.close')}
                            </button>
                        </div>
                    ) : step === 'verify' ? (
                        <>
                            {/* Header */}
                            <div className="modal-header">
                                <div className="modal-title">
                                    <div style={{
                                        width: 36, height: 36, borderRadius: 'var(--radius-sm)',
                                        background: 'rgba(79,142,247,0.15)', color: 'var(--primary)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18
                                    }}>
                                        <MdEmail />
                                    </div>
                                    {t('register.verifyTitle')}
                                </div>
                                <button className="modal-close" onClick={onClose} id="register-modal-close">
                                    <MdClose />
                                </button>
                            </div>

                            {/* Body */}
                            <div className="modal-body">
                                <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
                                    {t('register.verifyCodeSent', { email: form.email.trim() })}
                                </p>
                                <form onSubmit={handleVerifyAndSubmit} noValidate>
                                    <div className="form-group">
                                        <input
                                            id="reg-verify-code"
                                            className="form-input"
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={6}
                                            placeholder={t('register.verifyCodePlaceholder')}
                                            value={code}
                                            onChange={e => {
                                                setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                                                if (codeError) setCodeError('');
                                            }}
                                            disabled={verifying}
                                            style={{ textAlign: 'center', fontSize: 22, letterSpacing: 6 }}
                                            autoFocus
                                        />
                                        {codeError && <div className="form-error">{codeError}</div>}
                                    </div>

                                    <button
                                        id="register-verify-btn"
                                        type="submit"
                                        className="login-btn"
                                        disabled={verifying}
                                        style={{ marginTop: 8 }}
                                    >
                                        {verifying ? (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                                                <span className="spinner spinner-sm" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} />
                                                {t('register.verifying')}
                                            </span>
                                        ) : t('register.verifyButton')}
                                    </button>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, fontSize: 13 }}>
                                        <button
                                            type="button"
                                            onClick={handleBackToForm}
                                            disabled={verifying}
                                            style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 0, fontFamily: 'Inter, sans-serif' }}
                                        >
                                            {t('register.backToForm')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleResend}
                                            disabled={verifying || resendCooldown > 0}
                                            style={{
                                                background: 'none', border: 'none', cursor: resendCooldown > 0 ? 'default' : 'pointer',
                                                color: resendCooldown > 0 ? 'var(--text-muted)' : 'var(--primary)',
                                                padding: 0, fontFamily: 'Inter, sans-serif', fontWeight: 600
                                            }}
                                        >
                                            {resendCooldown > 0
                                                ? t('register.resendWait', { seconds: resendCooldown })
                                                : t('register.resendCode')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Header */}
                            <div className="modal-header">
                                <div className="modal-title">
                                    <div style={{
                                        width: 36, height: 36, borderRadius: 'var(--radius-sm)',
                                        background: 'rgba(79,142,247,0.15)', color: 'var(--primary)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18
                                    }}>
                                        <MdAssignment />
                                    </div>
                                    {t('register.title')}
                                </div>
                                <button className="modal-close" onClick={onClose} id="register-modal-close">
                                    <MdClose />
                                </button>
                            </div>

                            {/* Body */}
                            <div className="modal-body">
                                <form onSubmit={handleSubmit} noValidate>
                                    {/* Kullanıcı adı */}
                                    <div className="form-group">
                                        <label className="form-label" htmlFor="reg-username">
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <MdPerson style={{ fontSize: 14 }} /> {t('register.username')} *
                                            </span>
                                        </label>
                                        <input
                                            id="reg-username"
                                            className="form-input"
                                            type="text"
                                            placeholder={t('register.usernamePlaceholder')}
                                            value={form.username}
                                            onChange={e => handleChange('username', e.target.value)}
                                            autoComplete="username"
                                            disabled={loading}
                                        />
                                        {errors.username && <div className="form-error">{errors.username}</div>}
                                    </div>

                                    {/* Bağlı olduğu firma (bilgi amaçlı) */}
                                    <div className="form-group">
                                        <label className="form-label" htmlFor="reg-company-hint">
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <MdLocationCity style={{ fontSize: 14 }} /> {t('register.companyHint')} *
                                            </span>
                                        </label>
                                        <input
                                            id="reg-company-hint"
                                            className="form-input"
                                            type="text"
                                            placeholder={t('register.companyHintPlaceholder')}
                                            value={form.companyHint}
                                            onChange={e => handleChange('companyHint', e.target.value)}
                                            disabled={loading}
                                        />
                                        {errors.companyHint && <div className="form-error">{errors.companyHint}</div>}
                                    </div>

                                    {/* Email + Phone */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                        <div className="form-group">
                                            <label className="form-label" htmlFor="reg-email">
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <MdEmail style={{ fontSize: 14 }} /> {t('register.email')} *
                                                </span>
                                            </label>
                                            <input
                                                id="reg-email"
                                                className="form-input"
                                                type="email"
                                                placeholder={t('register.emailPlaceholder')}
                                                value={form.email}
                                                onChange={e => handleChange('email', e.target.value)}
                                                disabled={loading}
                                            />
                                            {errors.email && <div className="form-error">{errors.email}</div>}
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label" htmlFor="reg-phone">
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <MdPhone style={{ fontSize: 14 }} /> {t('register.phone')} *
                                                </span>
                                            </label>
                                            <input
                                                id="reg-phone"
                                                className="form-input"
                                                type="tel"
                                                placeholder={t('register.phonePlaceholder')}
                                                value={form.phone}
                                                onChange={e => handleChange('phone', e.target.value)}
                                                disabled={loading}
                                            />
                                            {errors.phone && <div className="form-error">{errors.phone}</div>}
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0 20px', position: 'relative' }}>
                                        <span style={{
                                            position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
                                            background: 'var(--bg-card)', padding: '0 12px', fontSize: 11,
                                            color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase'
                                        }}>
                                            {t('addCompanyModal.accountInfo')}
                                        </span>
                                    </div>

                                    {/* Password + Confirm Password */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                        <div className="form-group">
                                            <label className="form-label" htmlFor="reg-password">
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <MdLock style={{ fontSize: 14 }} /> {t('register.password')} *
                                                </span>
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    id="reg-password"
                                                    className="form-input"
                                                    type={showPassword ? 'text' : 'password'}
                                                    placeholder={t('register.passwordPlaceholder')}
                                                    value={form.password}
                                                    onChange={e => handleChange('password', e.target.value)}
                                                    autoComplete="new-password"
                                                    disabled={loading}
                                                    style={{ paddingRight: 40 }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(s => !s)}
                                                    tabIndex={-1}
                                                    style={{
                                                        position: 'absolute', right: 12, top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        background: 'none', border: 'none',
                                                        color: 'var(--text-muted)', cursor: 'pointer', fontSize: 17,
                                                        display: 'flex', alignItems: 'center', padding: 0
                                                    }}
                                                >
                                                    {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                                                </button>
                                            </div>
                                            {errors.password && <div className="form-error">{errors.password}</div>}
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label" htmlFor="reg-confirm">
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <MdLock style={{ fontSize: 14 }} /> {t('register.confirmPassword')} *
                                                </span>
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    id="reg-confirm"
                                                    className="form-input"
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    placeholder={t('register.confirmPasswordPlaceholder')}
                                                    value={form.confirmPassword}
                                                    onChange={e => handleChange('confirmPassword', e.target.value)}
                                                    autoComplete="new-password"
                                                    disabled={loading}
                                                    style={{ paddingRight: 40 }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowConfirmPassword(s => !s)}
                                                    tabIndex={-1}
                                                    style={{
                                                        position: 'absolute', right: 12, top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        background: 'none', border: 'none',
                                                        color: 'var(--text-muted)', cursor: 'pointer', fontSize: 17,
                                                        display: 'flex', alignItems: 'center', padding: 0
                                                    }}
                                                >
                                                    {showConfirmPassword ? <MdVisibilityOff /> : <MdVisibility />}
                                                </button>
                                            </div>
                                            {errors.confirmPassword && <div className="form-error">{errors.confirmPassword}</div>}
                                        </div>
                                    </div>

                                    {/* KVKK Section */}
                                    <div style={{
                                        background: 'var(--bg-input)',
                                        border: `1px solid ${errors.kvkkAccepted ? 'rgba(239,68,68,0.4)' : 'var(--border)'}`,
                                        borderRadius: 'var(--radius-md)',
                                        padding: '16px',
                                        marginBottom: 20
                                    }}>
                                        <button
                                            type="button"
                                            onClick={() => setShowKvkk(s => !s)}
                                            style={{
                                                background: 'none', border: 'none', cursor: 'pointer',
                                                color: 'var(--primary)', fontSize: 13, fontWeight: 600,
                                                display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12,
                                                padding: 0, fontFamily: 'Inter, sans-serif'
                                            }}
                                            id="kvkk-toggle-btn"
                                        >
                                            <MdAssignment style={{ fontSize: 16 }} />
                                            {t('register.kvkkTitle')} {showKvkk ? '▲' : '▼'}
                                        </button>

                                        {showKvkk && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                style={{
                                                    background: 'var(--bg-surface)',
                                                    border: '1px solid var(--border)',
                                                    borderRadius: 'var(--radius-sm)',
                                                    padding: '12px 14px',
                                                    fontSize: 12,
                                                    color: 'var(--text-secondary)',
                                                    lineHeight: 1.7,
                                                    maxHeight: 200,
                                                    overflowY: 'auto',
                                                    marginBottom: 12,
                                                    whiteSpace: 'pre-wrap'
                                                }}
                                            >
                                                {kvkkText}
                                            </motion.div>
                                        )}

                                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                                            <input
                                                id="reg-kvkk"
                                                type="checkbox"
                                                checked={form.kvkkAccepted}
                                                onChange={e => handleChange('kvkkAccepted', e.target.checked)}
                                                disabled={loading}
                                                style={{ marginTop: 2, accentColor: 'var(--primary)', width: 16, height: 16, flexShrink: 0, cursor: 'pointer' }}
                                            />
                                            <span style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                                                {t('register.kvkkAccept')}
                                            </span>
                                        </label>
                                        {errors.kvkkAccepted && <div className="form-error" style={{ marginTop: 8 }}>{errors.kvkkAccepted}</div>}
                                    </div>

                                    {/* Submit */}
                                    <button
                                        id="register-submit-btn"
                                        type="submit"
                                        className="login-btn"
                                        disabled={loading}
                                        style={{ marginTop: 0 }}
                                    >
                                        {loading ? (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                                                <span className="spinner spinner-sm" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} />
                                                {t('register.sendingCode')}
                                            </span>
                                        ) : t('register.sendCodeButton')}
                                    </button>
                                </form>
                            </div>
                        </>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
